<template>
  <Transition name="aside">
    <div
      v-if="isOpened"
      class="c-aside-dialog-backdrop fixed inset-0 z-50 flex h-full w-full select-none items-start justify-end overflow-y-auto bg-black/70 backdrop-blur backdrop-filter"
      @mousedown="closeModal"
    >
      <div
        class="c-aside-dialog min-h-full min-w-full max-w-lg bg-white px-6 py-12 sm:min-w-lg sm:rounded-l-3xl sm:px-12 sm:py-24"
        @mousedown.stop
      >
        <button
          class="c-btn absolute right-6 top-6 z-10 grid h-10 w-10 shrink-0 bg-white/75 p-0"
          type="button"
          @click="closeModal"
        >
          <XMarkIcon class="m-auto h-8 w-8" />
        </button>

        <slot />
      </div>
    </div>
  </Transition>
</template>

<script lang="ts" setup>
import { watch, nextTick } from 'vue'
import { useEventListener } from '@vueuse/core'
import { XMarkIcon } from '@heroicons/vue/24/outline'

import { isClient } from '/@shared/app'

const props = defineProps<{
  isOpened?: boolean
}>()

const emit = defineEmits({ 'content-rendered': null, close: null })

/**
 * Emit content-rendered
 */
watch(
  () => props.isOpened,
  () => {
    nextTick(() => {
      emit('content-rendered')
    })
  }
)

/**
 * Modal close
 */
function closeModal() {
  emit('close')
}

if (isClient)
  useEventListener(window, 'keydown', function escHandler(e: KeyboardEvent) {
    if (!props.isOpened) return

    if (
      e.code === 'Escape' &&
      document.activeElement?.tagName.toLowerCase() !== 'input'
    )
      closeModal()
  })
</script>

<style lang="postcss">
.aside-enter-active,
.aside-leave-active {
  transition: opacity 150ms theme('transitionTimingFunction.out-sine');

  & .c-aside-dialog {
    transition-duration: 150ms;
    transition-property: opacity, transform;
  }
}

.aside-leave-active .c-aside-dialog {
  transition-timing-function: theme('transitionTimingFunction.in-sine');
}

.aside-enter-active .c-aside-dialog {
  transition-timing-function: theme('transitionTimingFunction.out-sine');
}

.aside-enter-from,
.aside-leave-to {
  opacity: 0;

  & .c-aside-dialog {
    opacity: 0;
  }
}

.aside-enter-from .c-aside-dialog {
  transform: translateX(5rem);
}

.aside-leave-to .c-aside-dialog {
  transform: translateX(3rem);
}
</style>
